import React, { useState } from 'react';
import { Send, CheckCircle, Info, AlertTriangle } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Checkbox } from './ui/checkbox';

const EnquirySection = () => {
  const [formData, setFormData] = useState({
    // Section 1 - Contact
    fullName: '',
    businessName: '',
    email: '',
    phone: '',
    postcode: '',
    // Section 2 - Driving & Experience
    hasLicence: '',
    licenceOver2Years: '',
    yearsExperience: '',
    twoPersonUse: '',
    // Section 3 - Vehicle Request
    startDate: '',
    duration: '',
    intendedUse: '',
    additionalInfo: '',
    // Section 4 & 5 - Declarations
    insuranceDeclaration: false,
    accurateInfo: false,
    tradeOnlyAcknowledge: false,
  });
  
  const [submitted, setSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState({});

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: null }));
    }
  };

  const handleSelectChange = (name, value) => {
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: null }));
    }
  };

  const handleCheckboxChange = (name, checked) => {
    setFormData((prev) => ({ ...prev, [name]: checked }));
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: null }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    
    // Required fields
    if (!formData.fullName) newErrors.fullName = 'Required';
    if (!formData.email) newErrors.email = 'Required';
    if (!formData.phone) newErrors.phone = 'Required';
    if (!formData.hasLicence) newErrors.hasLicence = 'Required';
    if (!formData.licenceOver2Years) newErrors.licenceOver2Years = 'Required';
    if (!formData.yearsExperience) newErrors.yearsExperience = 'Required';
    if (!formData.startDate) newErrors.startDate = 'Required';
    if (!formData.duration) newErrors.duration = 'Required';
    
    // Critical checkboxes
    if (!formData.insuranceDeclaration) newErrors.insuranceDeclaration = 'You must confirm you understand the insurance requirements';
    if (!formData.accurateInfo) newErrors.accurateInfo = 'Required';
    if (!formData.tradeOnlyAcknowledge) newErrors.tradeOnlyAcknowledge = 'Required';
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    setIsSubmitting(true);
    
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/enquiry`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });
      
      if (!response.ok) {
        throw new Error('Failed to submit enquiry');
      }
      
      const result = await response.json();
      console.log('Enquiry submitted:', result);
      setSubmitted(true);
    } catch (error) {
      console.error('Error submitting enquiry:', error);
      alert('There was an error submitting your enquiry. Please try again or contact us via WhatsApp.');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <section id="enquiry" className="py-20 lg:py-28 bg-white">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="bg-emerald-50 rounded-3xl p-12">
            <img 
              src="https://customer-assets.emergentagent.com/job_wfp-transit-lease/artifacts/41nr5knc_F975D878-74D0-409C-8954-495598ACD4D9.jpeg" 
              alt="WFP Sussex Vans" 
              className="h-28 sm:h-32 w-auto mx-auto mb-6 mix-blend-multiply"
            />
            <div className="w-20 h-20 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <CheckCircle className="w-10 h-10 text-emerald-600" />
            </div>
            <h2 className="text-3xl font-bold text-slate-900 mb-4">Enquiry Received</h2>
            <p className="text-lg text-slate-600 mb-4">
              Thank you for your enquiry. We will review your details and respond regarding availability.
            </p>
            <p className="text-slate-500 mb-8">
              Please note: Submission of this form does not guarantee availability or approval. 
              We will contact you to discuss next steps including insurance requirements.
            </p>
            <Button
              onClick={() => {
                setSubmitted(false);
                setFormData({
                  fullName: '',
                  businessName: '',
                  email: '',
                  phone: '',
                  postcode: '',
                  hasLicence: '',
                  licenceOver2Years: '',
                  yearsExperience: '',
                  twoPersonUse: '',
                  startDate: '',
                  duration: '',
                  intendedUse: '',
                  additionalInfo: '',
                  insuranceDeclaration: false,
                  accurateInfo: false,
                  tradeOnlyAcknowledge: false,
                });
              }}
              variant="outline"
              className="border-2 border-slate-300"
            >
              Submit Another Enquiry
            </Button>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section id="enquiry" className="py-20 lg:py-28 bg-white">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-8">
          <span className="inline-block bg-sky-100 text-sky-700 px-4 py-2 rounded-full text-sm font-semibold mb-4">
            Check Availability
          </span>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-slate-900 mb-4">
            Request Access
          </h2>
          <p className="text-lg text-slate-600 max-w-2xl mx-auto">
            Complete the form below to enquire about vehicle availability. We will review your details and respond promptly.
          </p>
        </div>

        {/* Disclaimer */}
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 mb-8 flex items-start gap-3">
          <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
          <p className="text-amber-800 text-sm">
            <strong>This is a trade-only vehicle lease.</strong> Submission of this form does not guarantee availability or approval.
          </p>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="bg-slate-50 rounded-3xl p-6 lg:p-10">
          
          {/* Section 1 - Contact Details */}
          <div className="mb-10">
            <h3 className="text-lg font-bold text-slate-900 mb-4 flex items-center gap-2">
              <span className="w-7 h-7 bg-sky-600 text-white rounded-full flex items-center justify-center text-sm">1</span>
              Contact Details
            </h3>
            <div className="grid sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="fullName" className="text-slate-700 font-medium">Full Name *</Label>
                <Input
                  id="fullName"
                  name="fullName"
                  type="text"
                  required
                  value={formData.fullName}
                  onChange={handleInputChange}
                  className={`bg-white ${errors.fullName ? 'border-red-500' : 'border-slate-200'}`}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="businessName" className="text-slate-700 font-medium">Business Name</Label>
                <Input
                  id="businessName"
                  name="businessName"
                  type="text"
                  value={formData.businessName}
                  onChange={handleInputChange}
                  className="bg-white border-slate-200"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="email" className="text-slate-700 font-medium">Email Address *</Label>
                <Input
                  id="email"
                  name="email"
                  type="email"
                  required
                  value={formData.email}
                  onChange={handleInputChange}
                  className={`bg-white ${errors.email ? 'border-red-500' : 'border-slate-200'}`}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="phone" className="text-slate-700 font-medium">Mobile Number *</Label>
                <Input
                  id="phone"
                  name="phone"
                  type="tel"
                  required
                  value={formData.phone}
                  onChange={handleInputChange}
                  placeholder="07XXX XXXXXX"
                  className={`bg-white ${errors.phone ? 'border-red-500' : 'border-slate-200'}`}
                />
              </div>
              <div className="space-y-2 sm:col-span-2 sm:max-w-xs">
                <Label htmlFor="postcode" className="text-slate-700 font-medium">Business Postcode</Label>
                <Input
                  id="postcode"
                  name="postcode"
                  type="text"
                  value={formData.postcode}
                  onChange={handleInputChange}
                  className="bg-white border-slate-200"
                />
              </div>
            </div>
          </div>

          {/* Section 2 - Driving & Experience */}
          <div className="mb-10">
            <h3 className="text-lg font-bold text-slate-900 mb-4 flex items-center gap-2">
              <span className="w-7 h-7 bg-sky-600 text-white rounded-full flex items-center justify-center text-sm">2</span>
              Driving & Experience
            </h3>
            <div className="grid sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-slate-700 font-medium">Full UK Driving Licence? *</Label>
                <Select onValueChange={(v) => handleSelectChange('hasLicence', v)} value={formData.hasLicence}>
                  <SelectTrigger className={`bg-white ${errors.hasLicence ? 'border-red-500' : 'border-slate-200'}`}>
                    <SelectValue placeholder="Select..." />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="yes">Yes</SelectItem>
                    <SelectItem value="no">No</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label className="text-slate-700 font-medium">Licence held 2+ years? *</Label>
                <Select onValueChange={(v) => handleSelectChange('licenceOver2Years', v)} value={formData.licenceOver2Years}>
                  <SelectTrigger className={`bg-white ${errors.licenceOver2Years ? 'border-red-500' : 'border-slate-200'}`}>
                    <SelectValue placeholder="Select..." />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="yes">Yes</SelectItem>
                    <SelectItem value="no">No</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label className="text-slate-700 font-medium">Window Cleaning Experience *</Label>
                <Select onValueChange={(v) => handleSelectChange('yearsExperience', v)} value={formData.yearsExperience}>
                  <SelectTrigger className={`bg-white ${errors.yearsExperience ? 'border-red-500' : 'border-slate-200'}`}>
                    <SelectValue placeholder="Select..." />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="0-1">Less than 1 year</SelectItem>
                    <SelectItem value="1-3">1-3 years</SelectItem>
                    <SelectItem value="3-5">3-5 years</SelectItem>
                    <SelectItem value="5+">5+ years</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label className="text-slate-700 font-medium">Two-person use required?</Label>
                <Select onValueChange={(v) => handleSelectChange('twoPersonUse', v)} value={formData.twoPersonUse}>
                  <SelectTrigger className="bg-white border-slate-200">
                    <SelectValue placeholder="Select..." />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="yes">Yes</SelectItem>
                    <SelectItem value="no">No</SelectItem>
                    <SelectItem value="maybe">Maybe / Not sure</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>

          {/* Section 3 - Vehicle Request */}
          <div className="mb-10">
            <h3 className="text-lg font-bold text-slate-900 mb-4 flex items-center gap-2">
              <span className="w-7 h-7 bg-sky-600 text-white rounded-full flex items-center justify-center text-sm">3</span>
              Vehicle Request
            </h3>
            <div className="grid sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="startDate" className="text-slate-700 font-medium">Preferred Start Date *</Label>
                <Input
                  id="startDate"
                  name="startDate"
                  type="date"
                  required
                  value={formData.startDate}
                  onChange={handleInputChange}
                  className={`bg-white ${errors.startDate ? 'border-red-500' : 'border-slate-200'}`}
                />
              </div>
              <div className="space-y-2">
                <Label className="text-slate-700 font-medium">Estimated Duration *</Label>
                <Select onValueChange={(v) => handleSelectChange('duration', v)} value={formData.duration}>
                  <SelectTrigger className={`bg-white ${errors.duration ? 'border-red-500' : 'border-slate-200'}`}>
                    <SelectValue placeholder="Select..." />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="1-3-days">1-3 days</SelectItem>
                    <SelectItem value="1-week">1 week</SelectItem>
                    <SelectItem value="2-weeks">2 weeks</SelectItem>
                    <SelectItem value="1-month">1 month</SelectItem>
                    <SelectItem value="longer">Longer term</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2 sm:col-span-2 sm:max-w-xs">
                <Label className="text-slate-700 font-medium">Intended Use</Label>
                <Select onValueChange={(v) => handleSelectChange('intendedUse', v)} value={formData.intendedUse}>
                  <SelectTrigger className="bg-white border-slate-200">
                    <SelectValue placeholder="Select..." />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="residential">Residential</SelectItem>
                    <SelectItem value="commercial">Commercial</SelectItem>
                    <SelectItem value="mixed">Mixed</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2 sm:col-span-2">
                <Label htmlFor="additionalInfo" className="text-slate-700 font-medium">Additional Information</Label>
                <Textarea
                  id="additionalInfo"
                  name="additionalInfo"
                  value={formData.additionalInfo}
                  onChange={handleInputChange}
                  placeholder="Tell us about your requirements, reason for needing the vehicle, any questions..."
                  rows={3}
                  className="bg-white border-slate-200 resize-none"
                />
              </div>
            </div>
          </div>

          {/* Section 4 - Insurance Declaration */}
          <div className="mb-10">
            <h3 className="text-lg font-bold text-slate-900 mb-4 flex items-center gap-2">
              <span className="w-7 h-7 bg-red-600 text-white rounded-full flex items-center justify-center text-sm">4</span>
              Insurance Declaration
              <span className="text-xs bg-red-100 text-red-700 px-2 py-1 rounded-full font-medium">CRITICAL</span>
            </h3>
            <div className={`bg-white border-2 rounded-xl p-4 ${errors.insuranceDeclaration ? 'border-red-500' : 'border-slate-200'}`}>
              <div className="flex items-start gap-3">
                <Checkbox
                  id="insuranceDeclaration"
                  checked={formData.insuranceDeclaration}
                  onCheckedChange={(checked) => handleCheckboxChange('insuranceDeclaration', checked)}
                  className="mt-1"
                />
                <Label htmlFor="insuranceDeclaration" className="text-slate-700 leading-relaxed cursor-pointer">
                  I confirm that I understand this vehicle is supplied under a <strong>short-term lease agreement</strong> and that <strong>I am responsible for arranging fully comprehensive motor insurance</strong> covering the specific vehicle for commercial window-cleaning use. I understand that <strong>no vehicle will be released without verified insurance in place</strong>.
                </Label>
              </div>
              {errors.insuranceDeclaration && (
                <p className="text-red-600 text-sm mt-2">{errors.insuranceDeclaration}</p>
              )}
            </div>
          </div>

          {/* Section 5 - Confirmation */}
          <div className="mb-10">
            <h3 className="text-lg font-bold text-slate-900 mb-4 flex items-center gap-2">
              <span className="w-7 h-7 bg-sky-600 text-white rounded-full flex items-center justify-center text-sm">5</span>
              Confirmation
            </h3>
            <div className="space-y-4">
              <div className={`bg-white border rounded-xl p-4 ${errors.accurateInfo ? 'border-red-500' : 'border-slate-200'}`}>
                <div className="flex items-start gap-3">
                  <Checkbox
                    id="accurateInfo"
                    checked={formData.accurateInfo}
                    onCheckedChange={(checked) => handleCheckboxChange('accurateInfo', checked)}
                    className="mt-1"
                  />
                  <Label htmlFor="accurateInfo" className="text-slate-700 cursor-pointer">
                    I confirm the information provided is accurate
                  </Label>
                </div>
              </div>
              <div className={`bg-white border rounded-xl p-4 ${errors.tradeOnlyAcknowledge ? 'border-red-500' : 'border-slate-200'}`}>
                <div className="flex items-start gap-3">
                  <Checkbox
                    id="tradeOnlyAcknowledge"
                    checked={formData.tradeOnlyAcknowledge}
                    onCheckedChange={(checked) => handleCheckboxChange('tradeOnlyAcknowledge', checked)}
                    className="mt-1"
                  />
                  <Label htmlFor="tradeOnlyAcknowledge" className="text-slate-700 cursor-pointer">
                    I understand this is a <strong>trade-only service</strong> and not a public self-drive hire service
                  </Label>
                </div>
              </div>
            </div>
          </div>

          {/* Submit Button */}
          <Button
            type="submit"
            disabled={isSubmitting}
            className="w-full bg-sky-600 hover:bg-sky-700 text-white py-6 text-lg font-semibold shadow-lg shadow-sky-600/25 hover:shadow-xl transition-all duration-300"
          >
            {isSubmitting ? (
              'Submitting...'
            ) : (
              <>
                <Send className="w-5 h-5 mr-2" />
                Submit Enquiry
              </>
            )}
          </Button>

          <p className="text-xs text-slate-500 text-center mt-4">
            By submitting this form, you confirm you are enquiring as a trade user for commercial window-cleaning purposes.
          </p>
        </form>
      </div>
    </section>
  );
};

export default EnquirySection;
